/*
 * File: ClinicalEditList.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays the list of widgets and allows you to edit the layout of the widgets in clinical
 */
import React, { Component, ReactNode } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Checkbox,
  withStyles,
  Grid,
  Theme,
  createStyles,
  CircularProgress
} from "@material-ui/core";

import { observer } from "mobx-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableLocation
} from "react-beautiful-dnd";
import ReorderIcon from "@material-ui/icons/Reorder";
import ClinicalStore from "./Store";
import { getSnapshot, Instance, clone } from "mobx-state-tree";
import { StoreContext } from "utils/useStore";
import { ClinicalPreferenceValue } from "services/preferences/models/ClinicalPreferenceValue";
import { WithStyles } from "@material-ui/styles";
import { ContextType } from "react";
import { sortedClinicalPreferences } from "services/preferences/models/ClinicalPreferences";
import { GridDirection } from "@material-ui/core";
import Preferences from "services/preferences/Store";
import Button from "@material-ui/core/Button";
import Api, { ClinicalOptionsResult } from "./Api";

const progressSize = 24;

const styles = (theme: Theme) =>
  createStyles({
    subHeader: {
      background: theme.palette.common.tableHeader,
      lineHeight: `${theme.spacing(4)}px`,
      position: "sticky"
    },
    listItem: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      maxHeight: "50px",
      marginBottom: theme.spacing(2)
    },
    list: {
      margin: theme.spacing(0, 2),
      minHeight: "100px"
    },
    fullWidth: {
      width: "100%"
    },
    resetButton: {
      width: "100%"
    },
    resetButtonWrapper: {
      position: "relative"
    },
    resetProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -(progressSize / 2),
      marginLeft: -(progressSize / 2)
    },
    resetContainer: {
      display: "flex",
      margin: theme.spacing(1),
      [theme.breakpoints.up("xs")]: {
        justifyContent: "flex-end"
      },
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column"
      }
    }
  });

const CheckboxItem = observer(
  ({ pref }: { pref: Instance<typeof ClinicalPreferenceValue> }) => {
    return <Checkbox checked={pref.showInView} color="primary" disableRipple />;
  }
);

const WidgetTitle = observer(({ text }: { text: ReactNode }) => {
  return <ListItemText primary={text} />;
});

const resetOrder = (list: Instance<typeof ClinicalPreferenceValue>[]) => {
  list.forEach((element, idx) => {
    element.setOrder(idx);
  });
};

interface ListItemProps extends WithStyles<typeof styles> {
  pref: Instance<typeof ClinicalPreferenceValue>;
  widgetId: string;
}

const ClinicalListItem = observer(
  ({ pref, classes, widgetId }: ListItemProps) => {
    const toggleShowInView = () => {
      pref.toggleShowInView();
    };
    return (
      <Draggable
        key={widgetId}
        draggableId={"draggable" + widgetId}
        index={pref.orderNumber}
      >
        {provided => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <ListItem
              button
              className={classes.listItem}
              style={{ background: "#" + pref.color }}
              onClick={toggleShowInView}
              disableRipple
            >
              <CheckboxItem pref={pref} />
              <WidgetTitle text={pref.title} />
              <div {...provided.dragHandleProps}>
                <ReorderIcon style={{ cursor: "move" }} />
              </div>
            </ListItem>
          </div>
        )}
      </Draggable>
    );
  }
);

interface Props extends WithStyles<typeof styles, true> {
  onRef(instance?: ClinicalEditListInner): void;
  prefs: Instance<typeof ClinicalPreferenceValue>[];
}

interface State {
  widgets: Instance<typeof ClinicalPreferenceValue>[];
  options?: ClinicalOptionsResult | Promise<void> | undefined;
  startedLoad?: "desktop" | "default" | undefined;
}

export class ClinicalEditListInner extends Component<Props, State> {
  static contextType = StoreContext;
  context!: ContextType<typeof StoreContext>;

  _store?: Instance<typeof ClinicalStore>;

  element?: HTMLElement;

  constructor(props: Props) {
    super(props);
    this.state = { widgets: props.prefs.map(p => clone(p)) };
  }

  get store() {
    if (this._store === undefined) {
      if (this.context === undefined) {
        throw new Error("ClinicalEditList is missing context");
      }

      this._store = clone(this.context.getStore(ClinicalStore));
    }
    return this._store;
  }

  getWidgetByKey(key: string) {
    return this.state.widgets.find(widget => widget.key === key);
  }

  getWidgetsByColumnId(columnId: number) {
    return this.state.widgets.filter(w => w.columnNumber === columnId);
  }

  reorderWidgets(
    source: DraggableLocation,
    destination: DraggableLocation | undefined
  ) {
    // dropped nowhere
    if (!destination) {
      return;
    }
    const current = this.state.widgets.filter(
      pref => pref.columnNumber === Number(source.droppableId)
    );
    const next = this.state.widgets.filter(
      pref => pref.columnNumber === Number(destination.droppableId)
    );
    const target = current[source.index];
    // moving to same list
    if (source.droppableId === destination.droppableId) {
      const startIndex = source.index;
      const endIndex = destination.index;

      if (startIndex === endIndex) {
        return;
      }

      const [removed] = current.splice(startIndex, 1);
      current.splice(endIndex, 0, removed);
      resetOrder(current);
    } else {
      // moving to different list
      target.setColumn(Number(destination.droppableId));
      // remove from original
      current.splice(source.index, 1);
      resetOrder(current);
      // insert into next
      next.splice(destination.index, 0, target);
      resetOrder(next);
    }
    this.setState(prev => ({
      ...prev,
      widgets: sortedClinicalPreferences(prev.widgets)
    }));
  }

  componentDidMount() {
    this.props.onRef(this);
    document.body.style.position = "fixed";
    const elements = document.getElementsByClassName("uiDialogContent");
    if (elements[0] instanceof HTMLElement) {
      if (elements.length > 0) {
        this.element = elements[0];
        this.element.style.padding = "0px";
      }
    } else {
      console.error(`Expected HTMLElement, got "${elements[0]}"`);
    }
  }

  componentWillUnmount() {
    document.body.style.position = "";
    if (this.element) this.element.style.padding = "12px";
    this.props.onRef(undefined);
  }

  onDragEnd = (event: DropResult) => {
    this.reorderWidgets(event.source, event.destination);
  };

  save = async () => {
    if (this.context === undefined) {
      throw new Error("ClinicalEditList is missing context");
    }

    await this.context.getStore(Preferences).apply(
      "clinicalSummary",
      this.state.widgets.map(w => getSnapshot(w))
    );
  };

  isLoadingOptions = () => {
    return this.state.options instanceof Promise;
  };

  resetOption = (type: "default" | "desktop") => {
    return () => {
      if (this.state.options instanceof Promise) {
        return;
      } else if (this.state.options !== undefined) {
        this.performReset(this.state.options, type);
        return;
      }

      const promise = Api.getClinicalOptions().then(options => {
        this.performReset(options, type);
      });
      this.setState(prev => ({ ...prev, options: promise, startedLoad: type }));
    };
  };

  performReset = (
    options: ClinicalOptionsResult,
    startedBy: "desktop" | "default"
  ) => {
    let widgets: Instance<typeof ClinicalPreferenceValue>[];
    if (startedBy === "desktop") {
      widgets = sortedClinicalPreferences(
        options.desktop.map(opt => ClinicalPreferenceValue.create(opt))
      );
    } else if (startedBy === "default") {
      widgets = sortedClinicalPreferences(
        options.default.map(opt => ClinicalPreferenceValue.create(opt))
      );
    }

    this.setState(prev => ({
      ...prev,
      widgets,
      options,
      startedLoad: undefined
    }));
  };

  render() {
    const { classes } = this.props;
    let layoutDirection: GridDirection = "row";
    const firstSection = this.getWidgetsByColumnId(0);
    const secondSection = this.getWidgetsByColumnId(1);
    if (firstSection.length === 0 || secondSection.length === 0)
      layoutDirection = "column";
    return (
      <React.Fragment>
        <div className={classes.resetContainer}>
          <div className={classes.resetButtonWrapper}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={this.isLoadingOptions()}
              onClick={this.resetOption("default")}
              className={classes.resetButton}
            >
              Reset to Default
            </Button>
            {this.state.startedLoad === "default" && (
              <CircularProgress
                size={progressSize}
                className={classes.resetProgress}
              />
            )}
          </div>
          <div
            style={{
              flex: `0 0 ${this.props.theme.spacing(1)}px`
            }}
          />
          <div className={classes.resetButtonWrapper}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={this.isLoadingOptions()}
              onClick={this.resetOption("desktop")}
              className={classes.resetButton}
            >
              Reset to My Desktop Settings
            </Button>
            {this.state.startedLoad === "desktop" && (
              <CircularProgress
                size={progressSize}
                className={classes.resetProgress}
              />
            )}
          </div>
        </div>
        <Grid
          container
          direction={layoutDirection}
          justify="center"
          alignItems="stretch"
        >
          <DragDropContext onDragEnd={this.onDragEnd}>
            {[firstSection, secondSection].map((prefs, columnId) => {
              return (
                <Grid item xs={12} sm={6} key={columnId}>
                  <Droppable droppableId={columnId.toString()}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{ height: "100%" }}
                      >
                        <ListSubheader className={classes.subHeader}>
                          {columnId === 0 ? "First Column" : "Second Column"}
                        </ListSubheader>
                        <List className={classes.list}>
                          {prefs.map((pref, idx) => {
                            const widgetId =
                              columnId === 0 ? "first" + idx : "second" + idx;
                            return (
                              <ClinicalListItem
                                key={widgetId}
                                widgetId={widgetId}
                                pref={pref}
                                classes={classes}
                              />
                            );
                          })}
                          {provided.placeholder}
                        </List>
                      </div>
                    )}
                  </Droppable>
                </Grid>
              );
            })}
          </DragDropContext>
        </Grid>
      </React.Fragment>
    );
  }
}

export default withStyles(styles, {
  withTheme: true
})(observer(ClinicalEditListInner));
